import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import React, { useState, useEffect } from "react";
import { Spin, Typography, Card, Badge, List, Row, Col, Statistic, Tag, Divider, Tooltip, Button, message, Descriptions, Drawer, Timeline, Collapse } from "antd";
import {
    PhoneOutlined,
    UserOutlined,
    CalendarOutlined,
    SolutionOutlined,
    FileTextOutlined,
    CustomerServiceOutlined,
    InfoCircleOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    DatabaseOutlined,
    SyncOutlined,
    CopyOutlined,
    DownloadOutlined,
    ArrowLeftOutlined,
    BugOutlined,
    ThunderboltOutlined,
    RobotOutlined,
    ApiOutlined,
    MessageOutlined,
    WhatsAppOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { useParams, useNavigate } from "react-router-dom";
import StitchLayout from "../../components/Crm/CrmLayout";

const { Paragraph, Title, Text } = Typography;

const WHATSAPP_RATING_STYLES: Record<string, { color: string; bg: string }> = {
    Good: { color: '#389e0d', bg: '#f6ffed' },
    Average: { color: '#d48806', bg: '#fffbe6' },
    Poor: { color: '#cf1322', bg: '#fff1f0' },
};

const WHATSAPP_STATUS_LABELS: Record<string, string> = {
    feedback_requested: 'asked on WhatsApp, no reply yet',
    failed_to_request_feedback: 'WhatsApp could not be delivered',
};

interface CallDetailsData {
    call_analysis: {
        _id: string;
        analysis: {
            action_items: string[];
            call_metadata: {
                call_purpose: string;
                issue_resolved: string;
            };
            entities: {
                issue_category: string;
                item_id: string | null;
                order_id: string | null;
                suggested_issue_category: string;
            };
            executive_summary: string;
            next_best_action: string;
            priority: string;
            sentiment_and_profiling: {
                agent_professionalism_score: number;
                agent_tone: string;
                customer_satisfaction: string;
                customer_tone: string;
            };
            transcript: Array<{
                speaker: string;
                text: string;
                timestamp: string;
            }>;
        };
        audio_url: string;
        created_at: string;
        updated_at: string;
        ucid: string;
    };
    live_report: {
        Agent: string;
        AgentID: string;
        AgentStatus: string;
        AudioFile: string;
        CallDuration: string;
        CallerID: string;
        CampaignName: string;
        Comments: string | null;
        CustomerRingTime: string;
        CustomerStatus: string;
        DID: string;
        DialStatus: string;
        DialedNumber: string;
        Disposition: string;
        Duration: string;
        E164: string;
        EndTime: string;
        FallBackRule: string;
        HandlingTime: string;
        HangupBy: string;
        HoldDuration: string;
        Location: string | null;
        QueueTime: string;
        RateID: string | null;
        Rating: string;
        RatingComments: string | null;
        SNO: number;
        Skill: string;
        SkillID: string | null;
        StartTime: string;
        Status: string;
        TimeToAnswer: string;
        TransferType: string | null;
        TransferredTo: string | null;
        Type: string;
        UCID: string;
        UUI: string | null;
        WrapUpDuration: string;
        _id: string;
        monitorUCID: string;
        ivr_feedback?: {
            OptionResponse?: 'Satisfied' | 'Not_Satisfied' | 'No_input' | string;
        };
    };
    // Post-call WhatsApp reply, keyed by UCID in its own collection
    whatsapp_feedback?: {
        ucid?: string;
        agent?: string;
        status?: string;
        feedback_rating?: 'Good' | 'Average' | 'Poor' | string;
        feedback_received_at?: string;
        created_at?: string;
    } | null;
    voice_telemetry_logs?: {
        events: Array<{
            type: string;
            timestamp: string;
            details: any;
        }>;
        metrics?: {
            turn_count: number;
            tool_use_count: number;
            interruption_count: number;
            total_duration_sec: number;
            token_usage: { input_tokens: number; output_tokens: number; total_tokens: number };
        };
        status?: string;
        mode?: string;
        start_time?: string;
        end_time?: string;
    };
}

const OzonetelCallDetailsPage: React.FC = () => {
    const { ucid } = useParams<{ ucid: string }>();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [reAnalyzing, setReAnalyzing] = useState(false);
    const [data, setData] = useState<CallDetailsData | null>(null);
    const [telemetryOpen, setTelemetryOpen] = useState(false);

    useEffect(() => {
        if (ucid) {
            fetchCallDetails(ucid);
        }
    }, [ucid]);

    const fetchCallDetails = async (ucidToFetch: string) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`${apis.OZONETEL_LIVE_REPORT_DETAILS}?ucid=${ucidToFetch}`);
            const result = response.data;
            if (result.status) {
                setData(result.data);
            }
        } catch (error) {
            console.error("Error fetching call details:", error);
        } finally {
            setLoading(false);
        }
    };

    const reAnalyze = async () => {
        if (!data || !ucid) return;
        const audioUrl = data.live_report?.AudioFile || data.call_analysis?.audio_url;
        setReAnalyzing(true);
        try {
            const response = await axiosInstance.post(apis.OZONETEL_ANALYZE_CALL_AUDIO, {
                url: audioUrl,
                ucid,
                re_analyze: true,
            });
            if (response.data?.status !== false) {
                message.success("Re-analysis triggered successfully. Refreshing data...");
                await fetchCallDetails(ucid);
            } else {
                message.error(response.data?.message || "Re-analysis failed.");
            }
        } catch (error) {
            console.error("Error re-analyzing call:", error);
            message.error("Failed to trigger re-analysis. Please try again.");
        } finally {
            setReAnalyzing(false);
        }
    };

    const buildTranscriptText = (): string => {
        if (!data || !ucid) return '';
        const transcript = data.call_analysis?.analysis?.transcript || [];
        const lines: string[] = [
            `Call Transcript — UCID: ${ucid}`,
            `Start: ${data.live_report?.StartTime}  End: ${data.live_report?.EndTime}`,
            '─'.repeat(60),
        ];
        transcript.forEach((entry) => {
            const time = entry.timestamp ? ` [${entry.timestamp}]` : '';
            lines.push(`${entry.speaker}${time}: ${entry.text}`);
        });
        return lines.join('\n');
    };

    const handleCopyTranscript = async () => {
        const text = buildTranscriptText();
        if (!text) { message.warning('No transcript available'); return; }
        await navigator.clipboard.writeText(text);
        message.success('Transcript copied to clipboard');
    };

    const handleDownloadTranscript = () => {
        const text = buildTranscriptText();
        if (!text || !ucid) { message.warning('No transcript available'); return; }
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transcript_${ucid}_${(data?.live_report?.StartTime || '').replace(/[: ]/g, '-')}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const renderSentimentBar = (score: number) => (
        <div className="flex items-center gap-2 w-full">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                    className={`h-2 rounded-full ${score >= 4 ? 'bg-green-500' : score >= 3 ? 'bg-blue-500' : 'bg-red-500'}`}
                    style={{ width: `${(score / 5) * 100}%` }}
                />
            </div>
            <span className="text-xs font-semibold w-6 text-right">{score}/5</span>
        </div>
    );

    return (
        <StitchLayout>
            <div className="space-y-4 max-w-[1600px] mx-auto p-4 bg-gray-50 min-h-[calc(100vh-64px)]">
                {/* Header */}
                <div className="flex justify-between items-center bg-white px-4 py-3 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-3">
                        <Button 
                            icon={<ArrowLeftOutlined />} 
                            onClick={() => navigate(-1)} 
                            type="text" 
                        />
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-base">
                            <PhoneOutlined />
                        </div>
                        <div>
                            <Title level={5} style={{ marginBottom: 0, letterSpacing: '-0.3px', display: 'flex', alignItems: 'center' }}>
                                Call Details 
                                <Tag color="blue" className="ml-2">{ucid}</Tag>
                            </Title>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        {data?.voice_telemetry_logs && (
                            <Button
                                icon={<BugOutlined />}
                                size="small"
                                onClick={() => setTelemetryOpen(true)}
                                style={{ borderColor: '#7c3aed', color: '#7c3aed' }}
                            >
                                Voice Telemetry
                            </Button>
                        )}
                        <Button
                            icon={<SyncOutlined spin={reAnalyzing} />}
                            loading={reAnalyzing}
                            onClick={reAnalyze}
                            disabled={!data || loading}
                            size="small"
                        >
                            Re-analyze
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <Spin size="large" tip="Loading comprehensive call details..." />
                    </div>
                ) : data ? (
                    <div className="space-y-4">
                        {/* Header Stats Row */}
                        <Card size="small" className="shadow-sm border-0">
                            <Row gutter={24} align="middle">
                                <Col span={3}>
                                    <Statistic title="Duration" value={data.live_report?.CallDuration} prefix={<ClockCircleOutlined />} valueStyle={{ fontSize: '1.25rem' }} />
                                </Col>
                                <Col span={3}>
                                    <Statistic
                                        title="Priority"
                                        value={data.call_analysis?.analysis?.priority}
                                        valueStyle={{ color: data.call_analysis?.analysis?.priority === 'High' ? '#cf1322' : '#389e0d', fontSize: '1.25rem' }}
                                    />
                                </Col>
                                <Col span={4}>
                                    <Statistic
                                        title="Issue Resolved"
                                        value={data.call_analysis?.analysis?.call_metadata?.issue_resolved}
                                        prefix={data.call_analysis?.analysis?.call_metadata?.issue_resolved === 'Yes' ? <CheckCircleOutlined className="text-green-500" /> : <WarningOutlined className="text-orange-500" />}
                                        valueStyle={{ fontSize: '1.25rem' }}
                                    />
                                </Col>
                                <Col span={4}>
                                    <Statistic title="Agent" value={data.live_report?.Agent} prefix={<UserOutlined />} valueStyle={{ fontSize: '1.25rem' }} />
                                </Col>
                                <Col span={4}>
                                    <Statistic
                                        title="Customer"
                                        value={data.live_report?.CallerID}
                                        prefix={<PhoneOutlined />}
                                        valueStyle={{ fontSize: '1.25rem' }}
                                        formatter={(value) => (
                                            <a href={`tel:${value}`} className="text-gray-900 hover:text-blue-600 hover:underline">
                                                {String(value).replace(/(\d{5})(\d{5})/, "$1 $2")}
                                            </a>
                                        )}
                                    />
                                </Col>
                                <Col span={6}>
                                    <div className="flex flex-col h-full justify-center">
                                        <span className="text-gray-500 text-xs uppercase mb-1">Call Time</span>
                                        <div className="text-sm font-medium">{moment(data.live_report?.StartTime).format("MMM DD, YYYY HH:mm:ss")}</div>
                                    </div>
                                </Col>
                            </Row>
                            {/* Customer feedback — IVR survey and the post-call WhatsApp reply */}
                            {(data.live_report?.ivr_feedback?.OptionResponse || data.whatsapp_feedback) && (
                                <div style={{ margin: '16px 0 0 0', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                    {data.live_report?.ivr_feedback?.OptionResponse && (
                                        <div style={{ padding: '10px 14px', background: '#f6f8fa', borderRadius: 6 }}>
                                            <strong>IVR Feedback:</strong> {data.live_report.ivr_feedback.OptionResponse.replace('_', ' ')}
                                        </div>
                                    )}
                                    {data.whatsapp_feedback && (() => {
                                        const rating = data.whatsapp_feedback.feedback_rating;
                                        const style = WHATSAPP_RATING_STYLES[rating ?? ''] ?? { color: '#595959', bg: '#f6f8fa' };
                                        return (
                                            <div style={{ padding: '10px 14px', background: style.bg, borderRadius: 6 }}>
                                                <WhatsAppOutlined style={{ marginRight: 6, color: '#25D366' }} />
                                                <strong>WhatsApp Feedback:</strong>{' '}
                                                {rating ? (
                                                    <>
                                                        <span style={{ color: style.color, fontWeight: 600 }}>{rating}</span>
                                                        {data.whatsapp_feedback.feedback_received_at && (
                                                            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                                                                replied {moment(data.whatsapp_feedback.feedback_received_at).format("MMM DD, HH:mm")}
                                                            </Text>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Text type="secondary">
                                                        {WHATSAPP_STATUS_LABELS[data.whatsapp_feedback.status ?? ''] ?? 'no reply yet'}
                                                    </Text>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                        </Card>

                        <Row gutter={16}>
                            {/* Left Column: AI Analysis */}
                            <Col xs={24} lg={8}>
                                <div className="space-y-4">
                                    <Card title={<><SolutionOutlined /> Executive Summary</>} size="small" className="shadow-sm border-gray-200">
                                        <Paragraph className="text-gray-700 text-sm mb-0 text-justify">
                                            {data.call_analysis?.analysis?.executive_summary}
                                        </Paragraph>
                                    </Card>

                                    <Card title={<><FileTextOutlined /> Call Context & Entities</>} size="small" className="shadow-sm border-gray-200">
                                        <Descriptions column={1} size="small" bordered className="text-xs">
                                            <Descriptions.Item label="Call Purpose">
                                                <span className="whitespace-pre-wrap">{data.call_analysis?.analysis?.call_metadata?.call_purpose}</span>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Issue Category">
                                                <Tag color="cyan">{data.call_analysis?.analysis?.entities?.issue_category}</Tag>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Suggested Issue">
                                                <Tag color="geekblue">{data.call_analysis?.analysis?.entities?.suggested_issue_category}</Tag>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Item ID">
                                                {data.call_analysis?.analysis?.entities?.item_id || <span className="text-gray-400">N/A</span>}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Order ID">
                                                {data.call_analysis?.analysis?.entities?.order_id || <span className="text-gray-400">N/A</span>}
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </Card>

                                    <Card title={<><CalendarOutlined /> Action Items & Next Steps</>} size="small" className="shadow-sm border-gray-200">
                                        <List
                                            size="small"
                                            split={false}
                                            dataSource={data.call_analysis?.analysis?.action_items}
                                            renderItem={item => (
                                                <List.Item className="px-0 py-1">
                                                    <div className="flex items-start gap-2">
                                                        <Badge status="processing" className="mt-1.5" />
                                                        <span className="text-sm text-gray-700 leading-snug">{item}</span>
                                                    </div>
                                                </List.Item>
                                            )}
                                        />
                                        <Divider className="my-3" />
                                        <div className="bg-blue-50 p-3 rounded border border-blue-100">
                                            <div className="text-xs font-bold text-blue-800 uppercase mb-1">Next Best Action</div>
                                            <div className="text-sm text-blue-900 leading-snug">
                                                {data.call_analysis?.analysis?.next_best_action}
                                            </div>
                                        </div>
                                    </Card>

                                    <Card title={<><UserOutlined /> Sentiment & Profiling</>} size="small" className="shadow-sm border-gray-200">
                                        <div className="space-y-4">
                                            <div>
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className="text-gray-500">Agent Professionalism</span>
                                                </div>
                                                {renderSentimentBar(data.call_analysis?.analysis?.sentiment_and_profiling?.agent_professionalism_score)}
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <span className="text-xs text-gray-500 block">Agent Tone</span>
                                                    <Tag color="default">{data.call_analysis?.analysis?.sentiment_and_profiling?.agent_tone}</Tag>
                                                </div>
                                                <div>
                                                    <span className="text-xs text-gray-500 block">Customer Tone</span>
                                                    <Tag color={data.call_analysis?.analysis?.sentiment_and_profiling?.customer_tone === 'Angry' ? 'red' : 'blue'}>
                                                        {data.call_analysis?.analysis?.sentiment_and_profiling?.customer_tone}
                                                    </Tag>
                                                </div>
                                                <div className="col-span-2">
                                                    <span className="text-xs text-gray-500 block">Customer Satisfaction</span>
                                                    <span className="font-semibold text-gray-800">{data.call_analysis?.analysis?.sentiment_and_profiling?.customer_satisfaction}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                            </Col>

                            {/* Middle Column: Live Report Technical Details */}
                            <Col xs={24} lg={8}>
                                <div className="space-y-4 flex flex-col">
                                    <Card title={<><InfoCircleOutlined /> Technical Details</>} size="small" className="shadow-sm border-gray-200">
                                        <Descriptions column={1} size="small" bordered className="text-xs">
                                            <Descriptions.Item label="Campaign">{data.live_report?.CampaignName}</Descriptions.Item>
                                            <Descriptions.Item label="Skill used">{data.live_report?.Skill || '-'}</Descriptions.Item>
                                            <Descriptions.Item label="DID / Dialed">{data.live_report?.DID} / {data.live_report?.DialedNumber}</Descriptions.Item>
                                            <Descriptions.Item label="Status">
                                                <Tag color={data.live_report?.Status === 'Answered' ? 'green' : 'red'}>{data.live_report?.Status}</Tag>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Dial Status">{data.live_report?.DialStatus}</Descriptions.Item>
                                            <Descriptions.Item label="Hangup By">{data.live_report?.HangupBy}</Descriptions.Item>
                                            <Descriptions.Item label="Agent ID">{data.live_report?.AgentID}</Descriptions.Item>
                                            <Descriptions.Item label="Agent Status">{data.live_report?.AgentStatus}</Descriptions.Item>

                                            <Descriptions.Item label="Start Time">{data.live_report?.StartTime}</Descriptions.Item>
                                            <Descriptions.Item label="End Time">{data.live_report?.EndTime}</Descriptions.Item>
                                            <Descriptions.Item label="Duration (Total)">{data.live_report?.Duration}</Descriptions.Item>
                                            <Descriptions.Item label="Time To Answer">{data.live_report?.TimeToAnswer}</Descriptions.Item>
                                            <Descriptions.Item label="Customer Ring">{data.live_report?.CustomerRingTime}</Descriptions.Item>
                                            <Descriptions.Item label="Queue Time">{data.live_report?.QueueTime}</Descriptions.Item>
                                            <Descriptions.Item label="Hold Duration">{data.live_report?.HoldDuration}</Descriptions.Item>
                                            <Descriptions.Item label="WrapUp Duration">{data.live_report?.WrapUpDuration}</Descriptions.Item>

                                            <Descriptions.Item label="Disposition" span={1}>
                                                <Tooltip title={data.live_report?.Disposition}>
                                                    <span className="truncate block max-w-[150px]">{data.live_report?.Disposition}</span>
                                                </Tooltip>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Monitor UCID" span={1}>
                                                <span className="text-xs text-gray-500 break-all">{data.live_report?.monitorUCID}</span>
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </Card>

                                    <Card title={<><DatabaseOutlined /> System Info</>} size="small" className="shadow-sm border-gray-200">
                                        <Descriptions column={1} size="small" bordered className="text-xs">
                                            <Descriptions.Item label="Analysis ID">
                                                <span className="text-xs text-gray-500 break-all">{data.call_analysis?._id}</span>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Live Report ID">
                                                <span className="text-xs text-gray-500 break-all">{data.live_report?._id}</span>
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Created At">
                                                {moment(data.call_analysis?.created_at).format("YYYY-MM-DD HH:mm:ss")}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Updated At">
                                                {moment(data.call_analysis?.updated_at).format("YYYY-MM-DD HH:mm:ss")}
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </Card>
                                </div>
                            </Col>

                            {/* Right Column: Transcript & Recording */}
                            <Col xs={24} lg={8}>
                                <div className="flex flex-col space-y-4">
                                    {(data.live_report?.AudioFile || data.call_analysis?.audio_url) && (
                                        <Card size="small" className="shadow-sm border-gray-200 bg-gray-50">
                                            <div className="flex items-center gap-3">
                                                <CustomerServiceOutlined className="text-lg text-blue-600" />
                                                <div className="flex-1">
                                                    <div className="text-xs font-semibold uppercase text-gray-500 mb-1">Call Recording</div>
                                                    <audio controls src={data.live_report?.AudioFile || data.call_analysis?.audio_url} className="w-full h-8" />
                                                </div>
                                            </div>
                                        </Card>
                                    )}

                                    <Card
                                        title="Transcript"
                                        extra={
                                            <div className="flex items-center gap-2">
                                                <Tooltip title="Copy Transcript">
                                                    <button
                                                        onClick={handleCopyTranscript}
                                                        className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 hover:text-blue-600 transition-colors"
                                                    >
                                                        <CopyOutlined />
                                                        <span>Copy</span>
                                                    </button>
                                                </Tooltip>
                                                <Tooltip title="Download as .txt">
                                                    <button
                                                        onClick={handleDownloadTranscript}
                                                        className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 hover:text-green-600 transition-colors"
                                                    >
                                                        <DownloadOutlined />
                                                        <span>.txt</span>
                                                    </button>
                                                </Tooltip>
                                            </div>
                                        }
                                        size="small"
                                        className="shadow-sm border-gray-200 flex-1 flex flex-col"
                                        bodyStyle={{ flex: 1, padding: '12px', minHeight: '400px' }}
                                    >
                                        <div className="space-y-4">
                                            {data.call_analysis?.analysis?.transcript?.map((entry, index) => (
                                                <div key={index} className={`flex flex-col ${['Moglix Agent', 'Agent', 'speaker1'].includes(entry.speaker) ? 'items-end' : 'items-start'}`}>
                                                    <div className={`max-w-[90%] rounded-lg p-3 shadow-sm ${['Moglix Agent', 'Agent', 'speaker1'].includes(entry.speaker)
                                                        ? 'bg-blue-600 text-white rounded-tr-none'
                                                        : 'bg-white text-gray-800 border border-gray-200 rounded-tl-none'
                                                        }`}>
                                                        <div className={`flex justify-between items-center mb-1 gap-4 text-xs ${['Moglix Agent', 'Agent', 'speaker1'].includes(entry.speaker) ? 'text-blue-100' : 'text-gray-500'
                                                            }`}>
                                                            <span className="font-bold">{entry.speaker}</span>
                                                            <span className="opacity-75">{entry.timestamp || ''}</span>
                                                        </div>
                                                        <p className="text-sm m-0 leading-relaxed whitespace-pre-wrap">{entry.text}</p>
                                                    </div>
                                                </div>
                                            ))}
                                            {(!data.call_analysis?.analysis?.transcript || data.call_analysis.analysis.transcript.length === 0) && (
                                                <div className="text-gray-400 text-center py-4 text-xs">No transcript available</div>
                                            )}
                                        </div>
                                    </Card>
                                </div>
                            </Col>
                        </Row>
                    </div>
                ) : (
                    <div className="text-center py-20 text-gray-400">
                        No details available for this call.
                    </div>
                )}
            </div>

            {/* Voice Telemetry Debug Drawer */}
            <Drawer
                title={
                    <div className="flex items-center gap-2">
                        <BugOutlined style={{ color: '#6366f1' }} />
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>Voice Telemetry Logs</span>
                        <Tag color="geekblue" style={{ marginLeft: 4, fontFamily: 'monospace', fontSize: 11 }}>{ucid}</Tag>
                    </div>
                }
                open={telemetryOpen}
                onClose={() => setTelemetryOpen(false)}
                width={700}
                bodyStyle={{ padding: '16px 20px', background: '#f8fafc' }}
                headerStyle={{ background: '#fff', borderBottom: '1px solid #e2e8f0' }}
            >
                {data?.voice_telemetry_logs && (() => {
                    const logs = data.voice_telemetry_logs!;
                    const metrics = logs.metrics;

                    // Clean, professional color scheme
                    const eventConfig: Record<string, { accent: string; bg: string; labelColor: string; icon: React.ReactNode; label: string }> = {
                        AGENT_TURN:     { accent: '#3b82f6', bg: '#eff6ff', labelColor: '#1d4ed8', icon: <RobotOutlined />,       label: 'Agent' },
                        USER_TURN:      { accent: '#10b981', bg: '#f0fdf4', labelColor: '#047857', icon: <MessageOutlined />,     label: 'User' },
                        TOOL_CALL:      { accent: '#f59e0b', bg: '#fffbeb', labelColor: '#b45309', icon: <ApiOutlined />,         label: 'Tool Call' },
                        TOOL_RESULT:    { accent: '#8b5cf6', bg: '#f5f3ff', labelColor: '#6d28d9', icon: <ThunderboltOutlined />, label: 'Tool Result' },
                        USAGE_METADATA: { accent: '#94a3b8', bg: '#f8fafc', labelColor: '#64748b', icon: <DatabaseOutlined />,   label: 'Usage' },
                    };

                    return (
                        <div className="space-y-4">
                            {/* Metrics Summary */}
                            {metrics && (
                                <div
                                    className="rounded-xl p-4 grid grid-cols-3 gap-4"
                                    style={{ background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}
                                >
                                    {[
                                        { label: 'Turns', value: metrics.turn_count, color: '#3b82f6' },
                                        { label: 'Tool Calls', value: metrics.tool_use_count, color: '#f59e0b' },
                                        { label: 'Duration', value: `${metrics.total_duration_sec?.toFixed(1)}s`, color: '#10b981' },
                                        { label: 'Input Tokens', value: metrics.token_usage?.input_tokens?.toLocaleString(), color: '#6366f1' },
                                        { label: 'Output Tokens', value: metrics.token_usage?.output_tokens?.toLocaleString(), color: '#8b5cf6' },
                                        { label: 'Total Tokens', value: metrics.token_usage?.total_tokens?.toLocaleString(), color: '#1e293b' },
                                    ].map(m => (
                                        <div key={m.label} className="text-center py-1">
                                            <div style={{ fontSize: 20, fontWeight: 700, color: m.color, lineHeight: 1.2 }}>{m.value}</div>
                                            <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{m.label}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Event Timeline */}
                            <div className="space-y-2">
                                {logs.events.map((event, idx) => {
                                    const cfg = eventConfig[event.type] || { accent: '#94a3b8', bg: '#f8fafc', labelColor: '#64748b', icon: null, label: event.type };
                                    const time = event.timestamp ? moment(event.timestamp).format('HH:mm:ss.SSS') : '';

                                    const text = event.details?.text;
                                    const toolName = event.details?.tool;
                                    const toolArgs = event.details?.args;
                                    const toolResult = event.details?.result;
                                    const tokenUsage = event.details?.token_usage;

                                    return (
                                        <div
                                            key={idx}
                                            className="rounded-lg overflow-hidden"
                                            style={{
                                                background: cfg.bg,
                                                border: '1px solid #e2e8f0',
                                                borderLeft: `3px solid ${cfg.accent}`,
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                            }}
                                        >
                                            {/* Event header */}
                                            <div className="flex items-center gap-2 px-3 py-2">
                                                <span style={{ color: cfg.accent, fontSize: 12 }}>{cfg.icon}</span>
                                                <span style={{ color: cfg.labelColor, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                                                    {cfg.label}{toolName ? ` — ${toolName}` : ''}
                                                </span>
                                                <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: 10, fontFamily: 'monospace', letterSpacing: '0.02em' }}>{time}</span>
                                            </div>

                                            {/* Content */}
                                            {(text || tokenUsage || toolArgs || toolResult) && (
                                                <div className="px-3 pb-2.5" style={{ borderTop: `1px solid ${cfg.accent}18` }}>
                                                    {text && (
                                                        <p style={{ color: '#374151', fontSize: 13, margin: '8px 0 0', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{text}</p>
                                                    )}
                                                    {tokenUsage && (
                                                        <div className="flex gap-4 mt-2">
                                                            {Object.entries(tokenUsage).map(([k, v]) => (
                                                                <span key={k} style={{ fontSize: 11 }}>
                                                                    <span style={{ color: '#94a3b8' }}>{k.replace(/_tokens$/, '')}:</span>
                                                                    <span style={{ color: '#475569', fontWeight: 600, marginLeft: 3 }}>{String(v)}</span>
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {(toolArgs || toolResult) && (
                                                        <Collapse
                                                            ghost
                                                            size="small"
                                                            className="mt-1"
                                                            style={{ background: 'transparent' }}
                                                            items={[
                                                                ...(toolArgs ? [{
                                                                    key: 'args',
                                                                    label: <span style={{ color: '#b45309', fontSize: 11, fontWeight: 600 }}>▶ Args</span>,
                                                                    children: (
                                                                        <pre style={{ fontSize: 11, color: '#374151', background: '#fff', border: '1px solid #fde68a', padding: '8px 10px', borderRadius: 6, overflow: 'auto', margin: 0, lineHeight: 1.6 }}>
                                                                            {JSON.stringify(toolArgs, null, 2)}
                                                                        </pre>
                                                                    ),
                                                                }] : []),
                                                                ...(toolResult ? [{
                                                                    key: 'result',
                                                                    label: <span style={{ color: '#6d28d9', fontSize: 11, fontWeight: 600 }}>▶ Result</span>,
                                                                    children: (
                                                                        <pre style={{ fontSize: 11, color: '#374151', background: '#fff', border: '1px solid #ddd6fe', padding: '8px 10px', borderRadius: 6, overflow: 'auto', margin: 0, maxHeight: 280, lineHeight: 1.6 }}>
                                                                            {JSON.stringify(toolResult, null, 2)}
                                                                        </pre>
                                                                    ),
                                                                }] : []),
                                                            ]}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })()}
            </Drawer>
        </StitchLayout>
    );
};

export default OzonetelCallDetailsPage;
